<script setup lang="ts">
import { ulid } from "ulid";
import { ref, onMounted, onUnmounted } from "vue";

const props = withDefaults(
  defineProps<{
    placement?: "right" | "left";
    buttonType?: "roundedButton" | "angle" | "none";
    buttonClass?: string | Object;
    disable?: boolean;
  }>(),
  {
    disable: false,
    placement: "right",
    buttonType: "roundedButton",
  }
);

const open = ref(false);

const onClose = () => {
  open.value = false;
};

onMounted(async () => {
  const onEscape = (e: { key: string }) => {
    if (e.key === "Esc" || e.key === "Escape") {
      open.value = false;
    }
  };

  document.addEventListener("keydown", onEscape);

  onUnmounted(() => {
    document.removeEventListener("keydown", onEscape);
  });
});

defineExpose({
  onClose,
});
</script>

<template>
  <div class="relative">
    <button class="relative z-10 flex items-center w-full" @click="open = !open">
      <div class="group flex h-full w-full gap-1 transition-all duration-150">
        <slot name="button"></slot>
        <div v-if="!disable" v-show="props.buttonType === 'roundedButton'" class="flex items-center justify-center">
          <div
            :class="props.buttonClass"
            class="absolute mr-4 bg-item shadow-blue-shadow-deep group-hover:bg-item-hover group-hover:text-main-text-hover group-hover:shadow-blue-shadow-deep flex h-5 w-5 items-center justify-center rounded-full text-xs shadow-md transition-all duration-150 group-hover:shadow-lg"
          >
            <i class="fa-solid fa-angle-down mt-[0.1rem] text-main-text"></i>
          </div>
        </div>
        <div v-if="!disable" v-show="props.buttonType === 'angle'" :class="['flex items-center justify-center', props.buttonClass]">
          <div
            class="flex h-full items-center justify-center transition-all duration-150"
          >
            <i class="group absolute mr-4 fa-solid fa-angle-down text-lg"></i>
          </div>
        </div>
      </div>
    </button>

    <!-- to close when clicked on space around it in desktop-->
    <button
      class="focus:outline-hidden fixed inset-0 z-10 h-full w-full cursor-default"
      v-if="open"
      @click="open = false"
      tabindex="-1"
    ></button>
    <!--dropdown content: desktop-->
    <Transition
      enter-active-class="transition-all duration-150 ease-out"
      leave-active-class="transition-all duration-150 ease-in"
      enter-class="opacity-0"
      enter-to-class="opacity-100"
      leave-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        class="z-11 absolute bottom-0 left-0 right-0 block font-normal"
        v-show="open"
      >
        <div class="relative">
          <div :class="['absolute -top-1', placement === 'right' ? 'right-0' : 'left-0']">
            <div
              class="border-outline bg-item shadow-blue-shadow-deep mt-2 w-48 overflow-hidden rounded-md border text-sm shadow-lg"
            >
              <svg
                class="absolute top-[-0px] w-4"
                :class="placement === 'right' ? 'right-1' : 'left-1'"
                version="1.1"
                viewBox="0 0 14 8"
              >
                <polygon class="fill-menu-base" points="6.5 0 0 8 14 8 6.5 0" />
              </svg>
              <div class="bg-item grid gap-y-[1px]">
                <slot name="items" :onClose="onClose"></slot>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!--dropdown content: mobile-->
    <!-- <Transition
      enter-active-class="transition-all duration-200 ease-out"
      leave-active-class="transition-all duration-750 ease-in"
      enter-class="opacity-0"
      enter-to-class="opacity-100"
      leave-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div class="md:hidden fixed inset-x-0 bottom-0 bg-item w-full z-20 font-normal text-sm shadow-2xl leading-loose" v-if="open">
        <slot name="items" :onClose="onClose"></slot>
      </div>
    </Transition> -->
    <!-- to close when clicked on space around it in mobile-->
    <!-- <div class="md:hidden fixed w-full h-full inset-0 bg-gray-900 opacity-50 z-10" @click="open = false" v-if="open"></div> -->
  </div>
</template>
